// WO-GF-001 B4 — map style config. 1:1 port of legacy `snowbus-app/src/widgets/Map/component/option.js`
// (the live site's exact Google Maps styling). Zero interpretation: values copied verbatim from the
// rendered live machinery. Two modes, exactly as legacy:
//   - DEFAULT  (isMountain=false): cloud-styled map via mapId "4ec58074820d1b09" — transport / pickup /
//     getting-there maps. Legacy defaultZoom 13, center Vancouver (-123.1413127, 49.1698704).
//   - MOUNTAIN (isMountain=true):  JSON-styled map (the stylized blue/white theme below) — mountain
//     selection / mountain-context maps. Legacy defaultZoom 5. (mapId and inline `styles` are mutually
//     exclusive in the Google Maps API, so the two modes are genuinely distinct machinery, not a toggle.)

import type { MapProps } from "@vis.gl/react-google-maps";

// Legacy DefaultOption — google-map-react options for the non-mountain map.
export const DEFAULT_MAP_ID = "4ec58074820d1b09";

// Legacy default center + zoom (Map.js: defaultCenter {lng:-123.1413127, lat:49.1698704}; defaultZoom 13).
export const DEFAULT_CENTER = { lat: 49.1698704, lng: -123.1413127 } as const;
export const DEFAULT_ZOOM = 13;
export const MOUNTAIN_ZOOM = 5;

// Legacy MapOption.styles — the stylized theme applied to the mountain map. Copied verbatim from
// option.js (administrative/landscape/poi/road/transit/water tuned to the SnowBus blue/white palette).
export const MOUNTAIN_MAP_STYLES: NonNullable<MapProps["styles"]> = [
  { featureType: "administrative", elementType: "all", stylers: [{ visibility: "simplified" }] },
  { featureType: "administrative", elementType: "geometry.stroke", stylers: [{ visibility: "on" }] },
  { featureType: "administrative", elementType: "labels.text.fill", stylers: [{ color: "#162e4d" }] },
  { featureType: "administrative", elementType: "labels.icon", stylers: [{ color: "#4a90e2" }] },
  { featureType: "administrative.land_parcel", elementType: "all", stylers: [{ visibility: "off" }] },
  {
    featureType: "administrative.land_parcel",
    elementType: "geometry.stroke",
    stylers: [{ color: "#eceff5" }, { visibility: "on" }],
  },
  { featureType: "landscape", elementType: "all", stylers: [{ color: "#ffffff" }] },
  { featureType: "poi", elementType: "all", stylers: [{ visibility: "off" }] },
  { featureType: "road", elementType: "all", stylers: [{ saturation: -100 }, { lightness: 45 }] },
  { featureType: "road.highway", elementType: "all", stylers: [{ visibility: "simplified" }] },
  { featureType: "road.arterial", elementType: "labels.icon", stylers: [{ visibility: "off" }] },
  { featureType: "transit", elementType: "all", stylers: [{ visibility: "off" }] },
  { featureType: "water", elementType: "all", stylers: [{ color: "#d1e1f9" }, { visibility: "on" }] },
];

// Legacy bootstrapURLKeys.libraries — the exact set the live map loads (Map.js).
export const MAP_LIBRARIES = ["places", "geometry", "drawing", "visualization"] as const;

// ═══════════════════════════════════════════════════════════════════════════════════════════════════
// WO-GF-001-P1B-S29 · F185 — WHY THE MAP IS BLANK. Root-caused 2026-07-26 from the FRONTEND, per the
// founder's instruction not to re-litigate the key or the Cloud console. Read this before touching
// anything map-related; it answers "no tiles, no mountain geolocation" completely.
//
// The founder's banked fact was that ZERO requests reached Google in 7 days. That is correct, and there
// are TWO independent causes. They must not be conflated, because only one of them is ours.
//
// CAUSE 1 — OURS. THE FRONTEND GENUINELY NEVER CALLS GOOGLE.
//   `NEXT_PUBLIC_GMAPS_LIVE` is not set in .env (verified: `grep -c GMAPS_LIVE .env` → 0). So
//   `liveMapEnabled()` is false, `PortMap`'s `useLiveMap` is false, and `<APIProvider>` is NEVER
//   MOUNTED — the Maps JS API is not even fetched. No key error, no failed tile, no request at all.
//   This is by design (see liveMapEnabled below) but the DESIGN is what the founder is rejecting.
//   What renders instead is `PortMap`'s `MarkerOverlay`: a `display:flex; flex-wrap:wrap;
//   justify-content:center` box that lays the markers out in READING ORDER and IGNORES lat/lng
//   entirely. That is exactly "no tiles, no mountain geolocation" — the markers are not mispositioned,
//   they were never positioned. Confirmed by eye in the R1a selector at 700px: 26 named pink triangles
//   in tidy rows on a flat gradient.
//   The Static Maps path (`staticMapUrl`, S24 · F151) is NOT behind the flag and does reach Google —
//   but it is wired into exactly ONE call site, `BookingResults.tsx:688`, the per-stop thumbnails inside
//   an EXPANDED booking card. Nothing on the mountain selector, the hub "GETTING THERE" map or the
//   funnel map calls it. So the only Google traffic the app can produce is buried three interactions
//   deep, which is why the 7-day total reads as zero.
//
// CAUSE 2 — ENVIRONMENTAL, FOUNDER RAIL. THE KEY IN .env IS NOT THE PAID KEY.
//   `NEXT_PUBLIC_GMAPS_KEY` IS present (39 chars, AIzaSy…). Tested read-only against the Static Maps
//   API on 2026-07-26 with no referrer header, so referrer restrictions cannot be the explanation:
//     GET /maps/api/staticmap?center=Whistler,BC&zoom=8&size=320x200 → HTTP 403, text/plain:
//     "The Google Maps Platform server rejected your request. You must enable Billing on the Google
//      Cloud Project."
//   The founder's key is on project snowbus-353406 with billing PAID and Maps JS + Static ON. A 403
//   naming BILLING means the key in this .env belongs to a DIFFERENT, unbilled project. That also
//   reconciles the two facts that looked contradictory: the founder sees no traffic on snowbus-353406
//   because we never call it AND because the key we hold is not its key.
//   → Nothing in the frontend can make this key paint. Supplying the snowbus-353406 browser key is a
//     one-value founder move; no billing work is implied.
//
// CONSEQUENCE FOR THE BUILD. The geolocation DATA is already real and already served: GET
// /api/transport/geo returns 23 mountains and 22 stops with genuine lat/lng out of the legacy DB
// (e.g. big-white 49.7417/-118.9345). Nothing needs inventing to plot them — S29 deliberately did not
// add a single coordinate. What is missing is a STATIC ORIENTATION MAP (the founder's explicit want,
// "do not build an interactive surface to satisfy this") wired to the selector and hub surfaces with
// those coordinates as `markers=` params, replacing the reading-order overlay.
// That build is NOT shipped by S29, and the reason is the rule in the WO: it cannot be verified as the
// user until a billed key exists — every image would 403 on this machine, so "it works" would be an
// assertion, not an observation. Shipping an unverifiable map is the stub the WO forbids.
// ═══════════════════════════════════════════════════════════════════════════════════════════════════

// The live map key is a referrer-restricted CLIENT key (legacy AppConstants.mapApiKey). It is read from
// env, NEVER committed. fe-mono .env already carries NEXT_PUBLIC_GMAPS_KEY (verified 2026-07-20 — the WO
// note "not yet in .env" was stale); NEXT_PUBLIC_GOOGLE_MAPS_KEY is accepted as an alias. Without a key
// the map renders a faithful placeholder and the gate masks the region (date-picker precedent). NOTE: a
// referrer-restricted key only serves tiles on its allowed hosts (snowbus.com) — on localhost it may
// return RefererNotAllowedMapError, so the region stays masked until the port's host is allow-listed.
export function mapApiKey(): string | undefined {
  const k = process.env.NEXT_PUBLIC_GMAPS_KEY || process.env.NEXT_PUBLIC_GOOGLE_MAPS_KEY;
  return k && k.trim() ? k.trim() : undefined;
}

// Whether to actually MOUNT the live Google Map (tiles). Opt-in via NEXT_PUBLIC_GMAPS_LIVE=1 for hosts
// where the key is allow-listed (snowbus.com production). Default OFF because the legacy key is referrer-
// restricted to snowbus.com — on the port's localhost the map cannot paint tiles (a founder-side Google
// Cloud matter: referrer allow-list + mapId + billing), and mounting a live map that can't paint just
// holds a hanging connection open (breaks the harness's networkidle) for no visual gain. With the flag OFF
// the interactive marker overlay is shown (masked in the gate, tiles pending) — exactly the WO-B4 intent.
// The machinery is fully built + verified (the APIProvider bootstraps Google Maps with the exact legacy
// libraries; window.google.maps loads) and one env flag from live tiles.
export function liveMapEnabled(): boolean {
  const v = (process.env.NEXT_PUBLIC_GMAPS_LIVE || "").trim().toLowerCase();
  return v === "1" || v === "true" || v === "yes";
}

// WO-GF-001-S24 · F151/W2 — STATIC ORIENTATION MAP.
//
// Founder correction (2026-07-24): the funnel MAP is a static orientation image, not the interactive JS
// map — and the Snowbus Google Cloud project has billing ON + Static/JS Maps APIs enabled, so the only gap
// was that the frontend never CALLED Maps. This builds a Maps Static API image URL (low detail) for a stop
// or mountain, geocoded by address string so no lat/lng is needed. It is NOT behind the GMAPS_LIVE opt-in
// (that gate is for the hanging interactive JS map) — a static image either paints or 404s, harmlessly.
//
// The key is NEXT_PUBLIC_GMAPS_KEY. If that key is referrer-restricted to a different project (the current
// local key is), the image 403s on localhost — the caller then shows the marker placeholder and the
// close-out flags that the billing-backed Snowbus-project key belongs in .env (a key value, no billing work).
// ⚠ RETIRED 2026-07-27 (WO-GF-001-P1B-S31 · F185) — THIS FUNCTION HAS NO CALL SITES.
// Its one caller (BookingResults `StopMap`) now renders the keyless map from the stop's REAL served
// coordinate via /api/transport/geo, so nothing in the tree can reach Google Maps at runtime any more —
// proven by the gate, which counts Google Maps requests during a full selector drive and reports 0.
// It is kept rather than deleted per the standing "archive, never delete" rule, and banner-marked so a
// future `grep maps.googleapis.com` finds this note instead of concluding the app still calls Google.
// If a paid basemap is ever wanted, repoint MAP_STYLE_URL in `@/lib/map-config` — do not revive this.
export function staticMapUrl(
  query: string,
  opts: { zoom?: number; width?: number; height?: number; scale?: number; markerColor?: string } = {},
): string | null {
  const key = mapApiKey();
  if (!key) return null;
  const addr = `${query}, BC, Canada`;
  const { zoom = 12, width = 320, height = 160, scale = 2, markerColor = "0xF15A22" } = opts;
  const p = new URLSearchParams({
    center: addr,
    zoom: String(zoom),
    size: `${width}x${height}`,
    scale: String(scale),
    maptype: "roadmap",
    markers: `color:${markerColor}|${addr}`,
    key,
  });
  // WO-GF-001-S25 · W2 — MINIMAL-DETAIL ORIENTATION (founder: "less detail the better"). Strip the clutter
  // a full roadmap carries so the image reads as an orientation reference, not a street map: hide POIs and
  // transit, drop road + landscape labels, and simplify the administrative labels. These `style` rules are
  // repeated params (URLSearchParams.append), applied on top of the base roadmap. Purely a visual reduction
  // — the center/marker/geocoding are unchanged, so a working key still paints the same place, just cleaner.
  for (const s of [
    "feature:poi|visibility:off",
    "feature:transit|visibility:off",
    "feature:road|element:labels|visibility:off",
    "feature:landscape|element:labels|visibility:off",
    "feature:administrative|element:labels|visibility:simplified",
  ]) {
    p.append("style", s);
  }
  return `https://maps.googleapis.com/maps/api/staticmap?${p.toString()}`;
}
